// redux/Slices/managedAppsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";

const initialState = {
  list: [],
  data: {},
  error: null,
  siteNameAvailability: {
    isChecking: false,
    isAvailable: null,
    message: null
  }
};

// Create thunks
const checkSiteNameAvailability = createAsyncThunk(
  "managedApps/checkSiteName",
  async (siteName, { dispatch }) => {
    try {
      const res = await api("get", `managed-apps/check-name?siteName=${siteName}`);
      if (res?.success) {
        return res.data;
      }
      throw new Error(res.message || "Failed to check site name availability");
    } catch (error) {
      throw error;
    }
  }
);

const getManagedApps = createAsyncThunk(
  "managedApps/getAll",
  async (_, { dispatch, getState }) => {
    const state = getState();
    const activeOrg = state.org.active;

    if (!activeOrg?.id) {
      dispatch(showToast({ severity: "error", summary: "No active organization found" }));
      return Promise.reject("No active organization found");
    }

    try {
      const res = await api("get", `managed-apps/${activeOrg.id}`);

      if (res?.success && Array.isArray(res?.data)) {
        return res.data;
      } else {
        dispatch(showToast({ 
          severity: "error", 
          summary: "Invalid response format", 
          detail: "The server response was not in the expected format" 
        }));
        return Promise.reject("Invalid response format");
      }
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: "Failed to fetch applications", 
        detail: error.message 
      }));
      return Promise.reject(error.message || "An error occurred.");
    } 
  }
);

const deployManagedApp = createAsyncThunk(
  "managedApps/deploy",
  async (data, { dispatch, getState }) => {
    const state = getState();
    const activeOrg = state.org.active;

    if (!activeOrg?.id) {
      dispatch(showToast({ severity: "error", summary: "No active organization found" }));
      return Promise.reject("No active organization found");
    }

    try {
      // Check if this is a custom domain deployment
      const isCustomDomain = data.usePandastackDomain === false;
      
      // If custom domain, use deploy_minimal action
      const payload = { 
        ...data, 
        organization_id: activeOrg.id,
        action: isCustomDomain ? 'deploy_minimal' : 'deploy'
      };
      
      const res = await api("post", `managed-apps/deploy`, payload);

      if (res?.success && res?.data) {
        dispatch(showToast({ 
          severity: "success", 
          summary: isCustomDomain 
            ? "VM created successfully. Please verify your domain to complete installation."
            : "Application deployment queued successfully",
          persistent: true
        }));
        return res.data;
      }
      throw new Error(res.message || "Failed to deploy application");
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to deploy application",
        persistent: true
      }));
      throw error;
    }
  }
);

const redeployManagedApp = createAsyncThunk(
  "managedApps/redeploy",
  async ({ id, data }, { dispatch, getState }) => {
    const state = getState();
    const activeOrg = state.org.active;

    if (!activeOrg?.id) {
      dispatch(showToast({ severity: "error", summary: "No active organization found" }));
      return Promise.reject("No active organization found");
    }

    
    try {
      const payload = { 
        ...data, 
        organization_id: activeOrg.id 
      };
      const res = await api("post", `managed-apps/retry/${id}`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Application re-deployment queued successfully",
          persistent: true
        }));
        // Override the response tier with the new tier
        const updatedData = {
          ...res.data,
          tier: payload.tier,  // Use the tier from our request payload
          id
        };
        return updatedData;
      }
      throw new Error(res.message || "Failed to re-deploy application");
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to re-deploy application",
        persistent: true
      }));
      throw error;
    }
  }
);

const changeManagedAppConfiguration = createAsyncThunk(
  "managedApps/changeConfiguration",
  async ({ id, data }, { dispatch, getState }) => {
    const state = getState();
    const activeOrg = state.org.active;

    if (!activeOrg?.id) {
      dispatch(showToast({ severity: "error", summary: "No active organization found" }));
      return Promise.reject("No active organization found");
    }

    try {
      const payload = { 
        ...data, 
        organization_id: activeOrg.id 
      };
      const res = await api("post", `managed-apps/${id}/configuration`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Application configuration update queued successfully",
          persistent: true
        }));
        // Override the response tier with the new tier
        const updatedData = {
          ...res.data,
          tier: payload.tier,  // Use the tier from our request payload
          id
        };
        return updatedData;
      }
      throw new Error(res.message || "Failed to update application configuration");
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to update application configuration",
        persistent: true
      }));
      throw error;
    }
  }
);

const updateManagedApp = createAsyncThunk(
  "managedApps/update",
  async ({ id, data }, { dispatch }) => {
    try {
      const res = await api("post", `managed-apps/update/${id}`, data);

      if (res?.success && res?.data) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Application updated successfully",
          persistent: true
        }));
        await dispatch(getManagedApps());
        return res.data;
      }
      throw new Error(res.message || "Failed to update application");
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to update application",
        persistent: true
      }));
      throw error;
    } 
  }
);

const deleteManagedApp = createAsyncThunk(
  "managedApps/delete",
  async (id, { dispatch }) => {
    try {
      const res = await api("delete", `managed-apps/${id}`);

      if (res?.success) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Application deletion initiated",
          persistent: true
        }));
        return id;
      }
      throw new Error(res.message || "Failed to delete application");
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to delete application",
        persistent: true
      }));
      throw error;
    }
  }
);

const checkDeploymentStatus = createAsyncThunk(
  'managedApps/checkStatus',
  async (deployment_uuid, { dispatch }) => {
    try {
      const res = await api('get', `managed-apps/status/${deployment_uuid}`);

      if (res?.success && res?.data) {
        // Refresh list for final states
        if (['running', 'failed', 'deleted'].includes(res.data.status)) {
          await dispatch(getManagedApps());
        }
        return res.data;
      }
      throw new Error(res.message || 'Failed to check deployment status');
    } catch (error) {
      console.error('Check status error:', error);
      throw error;
    }
  }
);
const verifyDomain = createAsyncThunk(
  'managedApps/verifyDomain',
  async ({ appId, domain }, { dispatch }) => {
    try {
      dispatch(showLoader({ isLoadSkelton: true, type: 'verifyDomain' }));
      const res = await api('get', `managed-apps/verify-domain/${appId}?domain=${domain}`);
      
      if (res?.success && res?.data) {
        return res.data;
      }
      throw new Error(res.message || 'Failed to verify domain');
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to verify domain",
        persistent: true
      }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);
const completeInstallation = createAsyncThunk(
  'managedApps/completeInstallation',
  async ({ appId, domain }, { dispatch }) => {
    try {
      dispatch(showLoader({ isLoadSkelton: true, type: 'appInstallation' }));
      const res = await api('post', `managed-apps/complete-installation/${appId}`, { domain });
      
      if (res?.success && res?.data) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Application installation completed successfully",
          persistent: true
        }));
        
        // Refresh the managed apps list to get the updated app status
        await dispatch(getManagedApps());
        
        return res.data;
      }
      throw new Error(res.message || 'Failed to complete application installation');
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to complete application installation",
        persistent: true
      }));
      throw error;
    } finally {
      dispatch(hideLoader());
    }
  }
);


const checkInstallationStatus = createAsyncThunk(
  'managedApps/checkInstallationStatus',
  async (app_id, { dispatch }) => {
    try {
      const res = await api('get', `managed-apps/installation-status/${app_id}`);

      if (res?.success && res?.data) {
        return res.data;
      }
      throw new Error(res.message || 'Failed to check installation status');
    } catch (error) {
      console.error('Check installation status error:', error);
      throw error;
    }
  }
);

const managedAppsSlice = createSlice({
  name: "managedApps",
  initialState,
  reducers: {
    resetSiteNameAvailability: (state) => {
      state.siteNameAvailability = {
        isChecking: false,
        isAvailable: null,
        message: null
      };
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getManagedApps.pending, (state) => {
        state.error = null;
      })
      .addCase(getManagedApps.fulfilled, (state, action) => {
        state.list = action.payload;
        state.error = null;
      })
      .addCase(getManagedApps.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deployManagedApp.fulfilled, (state, action) => {
        state.list.push({
          ...action.payload,
          status: 'pending',
          deploymentError: null,
          operation: 'deployment'
        });
      })
      .addCase(redeployManagedApp.fulfilled, (state, action) => {
        const index = state.list.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          const { tier, ...rest } = action.payload;  // Destructure to separate tier
          state.list[index] = {
            ...state.list[index],
            ...rest,  // Spread everything except tier
            tier,     // Set tier explicitly
            status: 'updating',
            deploymentError: null,
            operation: 'redeployment'
          };
        }
      })
      .addCase(changeManagedAppConfiguration.fulfilled, (state, action) => {
        const index = state.list.findIndex(app => app.id === action.payload.id);
        if (index !== -1) {
          const { tier, ...rest } = action.payload;  // Destructure to separate tier
          state.list[index] = {
            ...state.list[index],
            ...rest,  // Spread everything except tier
            tier,     // Set tier explicitly
            status: 'updating',
            deploymentError: null,
            operation: 'redeployment'
          };
        }
      })
      .addCase(deleteManagedApp.fulfilled, (state, action) => {
        const index = state.list.findIndex(app => app.id === action.payload);
        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            status: 'deleting',
            operation: 'deletion'
          };
        }
      })
      .addCase(checkDeploymentStatus.fulfilled, (state, action) => {
        const app = state.list.find(app => 
          app.deployment_uuid === action.payload.deployment_uuid
        );
        if (app) {
          // Only update status if it's a valid transition
          const validTransitions = {
            pending: ['deploying', 'failed'],
            deploying: ['running', 'failed', 'awaiting_domain_verification'],
            running: ['running', 'failed', 'deleting'],
            deleting: ['deleted', 'failed'],
            updating: ['deploying', 'failed']
          };

          const currentStatus = app.status;
          const newStatus = action.payload.status;

          // Only update if it's a valid transition
          if (!validTransitions[currentStatus] || validTransitions[currentStatus].includes(newStatus)) {
            // Preserve the tier while updating other fields
            const currentTier = app.tier;
            Object.assign(app, {
              ...action.payload,
              tier: currentTier,  // Keep the current tier
              status: newStatus,
              lastDeployedAt: action.payload.lastUpdate?.timestamp,
              deploymentError: action.payload.lastUpdate?.message,
              operation: action.payload.operation || app.operation
            });

            // Remove from list if deleted
            if (newStatus === 'deleted') {
              state.list = state.list.filter(a => a.id !== app.id);
            }
          }
        }
      })
      .addCase(checkSiteNameAvailability.pending, (state) => {
        state.siteNameAvailability = {
          isChecking: true,
          isAvailable: null,
          message: null
        };
      })
      .addCase(checkSiteNameAvailability.fulfilled, (state, action) => {
        state.siteNameAvailability = {
          isChecking: false,
          isAvailable: action.payload.available,
          message: action.payload.message
        };
      })
      .addCase(checkSiteNameAvailability.rejected, (state, action) => {
        state.siteNameAvailability = {
          isChecking: false,
          isAvailable: false,
          message: action.error.message
        };
      })
      .addCase(checkInstallationStatus.fulfilled, (state, action) => {
        // Find the app by ID
        const app = state.list.find(app => app.id === action.payload.id);
        if (app) {
          // Update the app's installationStatus
          app.installationStatus = action.payload.status;
          
          // If installation is complete and status is 'succeeded', update app status to 'running'
          if (action.payload.status === 'succeeded' && app.status === 'deploying') {
            app.status = 'running';
          }
          
          // If installation failed, update app status to 'failed'
          if (action.payload.status === 'failed' && app.status === 'deploying') {
            app.status = 'failed';
            app.deploymentError = 'Installation script failed';
          }
        }
      })
      .addCase(verifyDomain.fulfilled, (state, action) => {
        // We don't need to update state here, just handle the action success in the component
      })
      .addCase(completeInstallation.fulfilled, (state, action) => {
        // The getManagedApps thunk is already dispatched in the action to refresh the app list
      });
  }
});

// Export actions and reducer
export const { resetSiteNameAvailability, clearError } = managedAppsSlice.actions;
export default managedAppsSlice.reducer;

// Export thunks
export {
  checkSiteNameAvailability,
  getManagedApps,
  deployManagedApp,
  redeployManagedApp,
  updateManagedApp,
  deleteManagedApp,
  checkDeploymentStatus,
  checkInstallationStatus,
  verifyDomain,
  completeInstallation,
  changeManagedAppConfiguration
};
